import debounce from 'lodash.debounce';
import { CioAutocomplete } from '@constructor-io/constructorio-ui-autocomplete';
import '@constructor-io/constructorio-ui-autocomplete/styles.css';
import './cio-autocomplete-override.css';
import { h, render } from 'preact';
import ProductCard from '../utils/product-card';
import mapConstructorProduct from '../utils/map-constructor-product';

// preact

// components
import SearchProducts from './search-products';
import CustomizerStyleModal from '../customizer-style-modal/modal';

// hooks
import useCustomizerStyleModal from '../../hooks/useCustomizerStyleModal';

export default function initializeMenu(element, data) {
  const toggleMenu = element.querySelector('[data-menu-toggle]');
  const mainMenuContainer = element.querySelector('[data-main-container]');
  const secondMenuContainer = element.querySelector('[data-second-container]');
  const secondMenuClose = element.querySelector('[data-close-sub]');
  const mainMenuClose = element.querySelector('[data-close-menu]');
  const subMenuToggles = element.querySelectorAll('[data-sub-target]');
  const bothMenuClose = element.querySelector('[data-close-menu-sub]');
  const backface = element.querySelector('[data-backface]');
  const headerEl = element.querySelector('[data-header]');
  const searchForm = element.querySelector('[data-search]');
  const searchProducts = element.querySelector('[data-search-products]');
  const searchInput = element.querySelector('[data-search] input');
  const searchPanel = element.querySelector('[data-search-panel]');
  const searchReactMount = element.querySelector('[data-search-react]');
  const clearSearchButton = element.querySelector('[data-clear-search]');
  const desktopMenuParents = element.querySelectorAll('[data-menu-parent]');

  // Create overlay element for autocomplete
  let cioOverlay = document.getElementById('cio-autocomplete-overlay');
  if (!cioOverlay) {
    cioOverlay = document.createElement('div');
    cioOverlay.id = 'cio-autocomplete-overlay';
    cioOverlay.style.position = 'fixed';
    cioOverlay.style.top = '0';
    cioOverlay.style.left = '0';
    cioOverlay.style.width = '100vw';
    cioOverlay.style.height = '100vh';
    cioOverlay.style.background = 'rgba(0,0,0,0.35)';
    cioOverlay.style.display = 'none';
    cioOverlay.setAttribute('data-overlay', 'true');
    document.body.appendChild(cioOverlay);
  }

  const GlobalCustomizerStyleModal = () => {
    const {
      isCustomizerStyleModalOpen,
      isKidsGraphic,
      isHoodieGraphic,
      isTankGraphic,
      customizerBackgroundImgUrl,
      closeCustomizerStyleModal,
    } = useCustomizerStyleModal();

    return (
      <CustomizerStyleModal
        isOpen={isCustomizerStyleModalOpen}
        onClose={closeCustomizerStyleModal}
        isKidsGraphic={isKidsGraphic}
        isHoodieGraphic={isHoodieGraphic}
        isTankGraphic={isTankGraphic}
        customizerBackgroundImgUrl={customizerBackgroundImgUrl}
        customizerStyles={data.customizerStyles}
      />
    );
  };

  // Fallback state
  let constructorApiHealthy = true;

  const handleConstructorError = () => {
    constructorApiHealthy = false;
    // Show fallback UI
    if (searchReactMount) {
      searchReactMount.style.display = 'none';
    }
    if (searchForm) {
      searchForm.style.display = '';
    }
    if (searchProducts) {
      searchProducts.style.display = '';
    }
  };

  const initSearchProducts = () => {
    const apiKey = 'key_9cAuBaO6I0Z7gU7C';

    if (!apiKey || !constructorApiHealthy || !searchReactMount) {
      // Fallback to original
      if (searchForm) searchForm.style.display = '';
      if (searchProducts) searchProducts.style.display = '';
      render(<SearchProducts {...data} />, searchProducts);
      if (searchReactMount) searchReactMount.style.display = 'none';
      return;
    }

    // Hide fallback
    if (searchForm) searchForm.style.display = 'none';
    if (searchProducts) searchProducts.style.display = 'none';
    searchReactMount.style.display = '';

    // default search input from 'q' param
    const urlParams = new URLSearchParams(window.location.search);
    let defaultInput = '';
    if (!window.location.href.includes('customizer')) {
      defaultInput = urlParams.get('q') || '';
    }

    try {
      render(
        <CioAutocomplete
          apiKey={apiKey}
          placeholder="Find a product"
          defaultInput={defaultInput}
          cioJsClientOptions={{ segments: ['main_site'] }}
          onSubmit={(submitEvent) => {
            const value = submitEvent?.item?.value || submitEvent?.query || submitEvent?.originalQuery;

            window.location.href = `${window.location.origin}/search?q=${encodeURIComponent(value)}`;
          }}
          openOnFocus={true}
          onFocus={() => {
            setTimeout(() => {
              const menuEl = document.getElementById('cio-autocomplete-0-menu');
              const headerGroups = document.querySelectorAll('.shopify-section-group-header-group');
              const offset = window.innerWidth >= 1024 ? 55 : 5;

              if (menuEl && headerGroups.length > 0) {
                let totalHeight = 0;

                headerGroups.forEach((el) => {
                  totalHeight += el.offsetHeight;
                });

                menuEl.style.top = `${totalHeight - offset}px`;
              }
            }, 0);
          }}
          onIsOpenChange={(changes) => {
            if (changes.isOpen) {
              document.body.classList.add('scroll-lock--fixed');
              cioOverlay.style.display = 'block';
            } else {
              document.body.classList.remove('scroll-lock--fixed');
              cioOverlay.style.display = 'none';
            }
          }}
          advancedParameters={{
            displayShowAllResultsButton: true,
            fetchZeroStateOnFocus: true,
          }}
          zeroStateSections={[
            {
              indexSectionName: 'Search Suggestions',
              displayName: 'Search Suggestions',
              podId: 'popular-searches',
              type: 'recommendations',
              numResults: 10,
            },
            {
              displayName: 'Trending Now',
              podId: 'best-sellers',
              type: 'recommendations',
              numResults: 4,
              renderItem: ({ item, getItemProps }) => (
                <ProductCard
                  product={mapConstructorProduct(item)}
                  template={getItemProps.template || 'collection'}
                  saleCollection={getItemProps.saleCollection || 'false'}
                  layout={getItemProps.layout || { isAboveBreakpoint: () => true }}
                  openCustomizerStyleModal={getItemProps.openCustomizerStyleModal}
                  collectionHandle={getItemProps.collectionHandle}
                  isSearch={true}
                  {...getItemProps}
                />
              ),
            },
          ]}
          sections={[
            {
              indexSectionName: 'Search Suggestions',
              numResults: 8,
              displaySearchTermHighlights: true,
              displayNoResultsMessage: true,
            },
            {
              indexSectionName: 'Products',
              displayName: 'Top Suggestions',
              numResults: 4,
              displaySearchTermHighlights: true,
              displayNoResultsMessage: true,
              renderItem: ({ item, getItemProps }) => (
                <ProductCard
                  product={mapConstructorProduct(item)}
                  template={getItemProps.template || 'collection'}
                  saleCollection={getItemProps.saleCollection || 'false'}
                  layout={getItemProps.layout || { isAboveBreakpoint: () => true }}
                  openCustomizerStyleModal={getItemProps.openCustomizerStyleModal}
                  collectionHandle={getItemProps.collectionHandle}
                  isSearch={true}
                  {...getItemProps}
                />
              ),
            },
          ]}
          onError={handleConstructorError}
        />,
        searchReactMount,
      );

      // Add event listener for 'Show All Results' button
      setTimeout(() => {
        const showAllBtn = searchReactMount.querySelector('[data-cnstrc-search-submit-btn="true"]');
        if (showAllBtn) {
          showAllBtn.addEventListener('click', () => {
            const { value } = searchInput;
            if (value) {
              window.location.href = `${window.location.origin}/search?q=${encodeURIComponent(value)}`;
            }
          });
        }
      }, 0);
    } catch (err) {
      handleConstructorError();
    }
  };

  const init = () => {
    const debouncedResizeHandler = debounce(onResizeHandler, 500);
    window.addEventListener('resize', debouncedResizeHandler);
    onResizeHandler();
    // Initialize search UI
    initSearchProducts();

    desktopMenuParents.forEach((parent) => {
      const listItem = parent.closest('li');
      const submenu = listItem?.querySelector('[data-submenu]');

      if (!submenu) return;

      const openSubmenu = () => {
        parent.setAttribute('aria-expanded', 'true');
        submenu.setAttribute('aria-hidden', 'false');
        submenu.classList.remove('hidden', 'max-h-0', 'opacity-0');
        submenu.classList.add('flex', 'max-h-[1000px]', 'opacity-100');
      };

      const closeSubmenu = () => {
        parent.setAttribute('aria-expanded', 'false');
        submenu.setAttribute('aria-hidden', 'true');
        submenu.classList.remove('flex', 'max-h-[1000px]', 'opacity-100');
        submenu.classList.add('hidden', 'max-h-0', 'opacity-0');
      };

      parent.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const isExpanded = parent.getAttribute('aria-expanded') === 'true';

          if (isExpanded) {
            closeSubmenu();
          } else {
            openSubmenu();
          }
        }

        if (e.key === 'Escape') {
          closeSubmenu();
          parent.focus();
        }
      });

      listItem.addEventListener('mouseenter', () => {
        openSubmenu();
      });

      listItem.addEventListener('mouseleave', () => {
        closeSubmenu();
      });

      const menuItems = submenu.querySelectorAll('a[role="menuitem"]');
      if (menuItems.length > 0) {
        menuItems[menuItems.length - 1].addEventListener('keydown', (e) => {
          if (e.key === 'Tab' && !e.shiftKey) {
            closeSubmenu();
          }
        });
      }
    });

    searchInput.addEventListener('click', handleSearchPanel);
    searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        handleSearchPanel();
      }
    });
    searchInput.addEventListener('input', (e) => handleSearchResults(e));
    searchForm.addEventListener('submit', (e) => handleFormSubmit(e));
    clearSearchButton.addEventListener('click', (e) => {
      e.preventDefault();
      closeSearch();
    });

    document.addEventListener('click', (e) => handleHeaderClick(e));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeSearch(e);
      }
    });

    if (data.template !== 'product') {
      let lastScroll = 0;
      let isScrolled = false;
      window.addEventListener('scroll', () => {
        const headerSection = document.querySelector('.shopify-section-header');
        const currentScroll = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
        const scrollDirection = currentScroll < lastScroll;
        const shouldToggle = isScrolled && scrollDirection;
        isScrolled = currentScroll > 100;
        headerSection?.classList.toggle('sticky', shouldToggle);
        headerSection?.classList.toggle('animate-slideDown', shouldToggle);
        lastScroll = currentScroll;
      });
    }

    toggleMenu.addEventListener('click', () => {
      backface.classList.add('!block');
      mainMenuContainer.classList.add('left-slide');
      document.body.classList.add('scroll-lock');
    });

    subMenuToggles.forEach((toggle) => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        const searchedMenu = toggle.dataset.subTarget;
        const submenu = element.querySelector(`[data-sub-menu="${searchedMenu}"]`);

        toggle.setAttribute('aria-expanded', 'true');
        submenu.setAttribute('aria-hidden', 'false');

        secondMenuContainer.classList.add('left-slide');
        submenu.classList.add('show-flex');
      });

      toggle.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle.click();
        }
      });
    });

    secondMenuClose.addEventListener('click', (e) => {
      e.preventDefault();
      const openSubmenu = element.querySelector('.show-flex[data-sub-menu]');
      const openToggle = element.querySelector('[data-sub-target][aria-expanded="true"]');

      if (openSubmenu) {
        openSubmenu.classList.remove('show-flex');
        openSubmenu.setAttribute('aria-hidden', 'true');
      }
      if (openToggle) {
        openToggle.setAttribute('aria-expanded', 'false');
      }

      secondMenuContainer.classList.remove('left-slide');
    });

    mainMenuClose.addEventListener('click', (e) => {
      e.preventDefault();
      mainMenuContainer.classList.remove('left-slide');
      backface.classList.remove('!block');
      document.body.classList.remove('scroll-lock');
    });

    bothMenuClose.addEventListener('click', (e) => {
      e.preventDefault();
      const openSubmenu = element.querySelector('.show-flex[data-sub-menu]');
      const openToggle = element.querySelector('[data-sub-target][aria-expanded="true"]');

      if (openSubmenu) {
        openSubmenu.classList.remove('show-flex');
        openSubmenu.setAttribute('aria-hidden', 'true');
      }
      if (openToggle) {
        openToggle.setAttribute('aria-expanded', 'false');
      }

      secondMenuContainer.classList.remove('left-slide');
      mainMenuContainer.classList.remove('left-slide');
      backface.classList.remove('!block');
      document.body.classList.remove('scroll-lock');
    });

    backface.addEventListener('click', () => {
      const openSubmenu = element.querySelector('.show-flex[data-sub-menu]');
      const openToggle = element.querySelector('[data-sub-target][aria-expanded="true"]');

      if (openSubmenu) {
        openSubmenu.classList.remove('show-flex');
        openSubmenu.setAttribute('aria-hidden', 'true');
      }
      if (openToggle) {
        openToggle.setAttribute('aria-expanded', 'false');
      }

      secondMenuContainer.classList.remove('left-slide');
      mainMenuContainer.classList.remove('left-slide');
      backface.classList.remove('!block');
      document.body.classList.remove('scroll-lock');
    });
  };

  const handleSearchPanel = () => {
    searchPanel.classList.add('active');
    clearSearchButton.classList.remove('hidden');
    document.body.classList.add('scroll-lock--fixed');
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    window.location.href = `${window.location.origin}/search?q=${searchInput.value}`;
  };

  const closeSearch = () => {
    // console.log('Closing search');
    // searchPanel.classList.remove('active');
    // searchPanel.classList.remove('no-results');
    // document.body.classList.remove('scroll-lock--fixed');
    // searchInput.value = '';
    // clearSearchButton.classList.add('hidden');
  };

  const handleHeaderClick = (e) => {
    if (
      (!e.target.closest('[data-search]') && !e.target.closest('[data-search-panel]')) ||
      e.target.hasAttribute('data-overlay')
    ) {
      closeSearch();
    }
  };

  const handleSearchResults = (e) => {
    searchPanel.classList.remove('active');
    searchPanel.classList.add('no-results');
    document.body.classList.add('scroll-lock--fixed');
    clearSearchButton.classList.remove('hidden');
    const noResElement = element.querySelector('#no-res');
    if (noResElement) {
      noResElement.textContent = e.target.value;
    }
  };

  const onResizeHandler = () => {
    const viewportOffset = headerEl.getBoundingClientRect();
    document.documentElement.style.setProperty(
      '--header-spacer',
      `${headerEl.offsetHeight + viewportOffset.top || 0}px`,
    );
  };

  init();
  initSearchProducts();

  render(<GlobalCustomizerStyleModal />, element);
}
